import { Button } from '@navi/web-ui/lib/primitives';
import { RootState } from '@cp/src/store';
import { useDispatch, useSelector } from 'react-redux';
import MockStates from './MockActionStates';
import styles from './index.module.scss';
import { MOCK_RUN_HISTORY_FETCH_TYPE, MockControlState, MockStatus } from '../../../types';
import { fetchMockRunDetailsByStatus } from '../../../actions/FieldAllocationAction';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { SELF_SERVE_UI_CLICKSTREAM } from '@cp/src/service/clickStream.constant';

interface ISubmitCta {
  onSubmit: () => void;
}

const SubmitCta = (props: ISubmitCta) => {
  const { onSubmit } = props;
  const dispatch = useDispatch();
  const currentMockAllocationStatus = useSelector(
    (state: RootState) => state?.fieldAllocation?.fieldAllocationDetails?.status?.value
  );
  const showApproveMockButton =
    useSelector(
      (state: RootState) =>
        state?.fieldAllocation?.fieldAllocationDetails?.status?.value === MockStatus.COMPLETED
    ) || false;

  const hasMockApprovalPermission =
    useSelector(
      (store: RootState) => store.common.featureFlags?.approveSelfServeAllocationsEnabled
    ) || false;

  const isApproveMockAvailable =
    useSelector(
      (state: RootState) => state.fieldAllocation?.mockControlsStatus?.approveMock?.state
    ) === MockControlState.AVAILABLE;

  const mockRunCapacity = useSelector(
    (state: RootState) => state.fieldAllocation?.mockRunCapacity?.TRIGGERED
  );

  const mockId = useSelector(
    (state: RootState) => state?.fieldAllocation?.fieldAllocationDetails?.allocationTrackerId
  );

  const isMockTriggerAvailable =
    useSelector(
      (state: RootState) => state.fieldAllocation?.mockControlsStatus?.triggerMock?.state
    ) === MockControlState.AVAILABLE;

  const mockTriggerMessage = useSelector(
    (state: RootState) => state.fieldAllocation?.mockControlsStatus?.triggerMock?.message
  );

  const approveMockMessage = useSelector(
    (state: RootState) => state.fieldAllocation?.mockControlsStatus?.approveMock?.message
  );

  const { allowed: isTriggerMockAllowed = false, message: mockRunCapacityExceededMessage = '' } =
    mockRunCapacity || {};

  const isTriggerMockDisabled =
    !isTriggerMockAllowed ||
    currentMockAllocationStatus !== MockStatus.INPUTS_READY ||
    !isMockTriggerAvailable;

  if (
    currentMockAllocationStatus === MockStatus.APPROVED ||
    currentMockAllocationStatus === MockStatus.ALLOCATED
  )
    return null;

  if (showApproveMockButton) {
    const handleApproveMock = () => {
      addClickstreamEvent(SELF_SERVE_UI_CLICKSTREAM.LH_APPROVE_MOCK_CLICKED, {
        mockId: mockId
      });

      dispatch(
        fetchMockRunDetailsByStatus(MockStatus.APPROVED, MOCK_RUN_HISTORY_FETCH_TYPE.CURRENT_MONTH)
      );
      onSubmit();
    };
    return (
      <div className={styles.buttonContainer}>
        <Button
          disabled={!hasMockApprovalPermission || !isApproveMockAvailable}
          onClick={handleApproveMock}
          variant="primary"
        >
          <MockStates
            showTooltip={!hasMockApprovalPermission || !isApproveMockAvailable}
            buttonLabel={'Approve mock'}
            tooltipContent={approveMockMessage || 'You do not have access to approve allocation'}
          />
        </Button>
      </div>
    );
  }

  const showTooltip =
    !isTriggerMockAllowed &&
    !!mockRunCapacityExceededMessage &&
    currentMockAllocationStatus === MockStatus.INPUTS_READY;

  const handleTriggerMock = () => {
    addClickstreamEvent(SELF_SERVE_UI_CLICKSTREAM.LH_TRIGGER_MOCK_CLICKED, {
      mockId: mockId
    });

    onSubmit();
  };

  return (
    <div className={styles.buttonContainer}>
      <Button disabled={isTriggerMockDisabled} onClick={handleTriggerMock} variant="primary">
        <MockStates
          showTooltip={showTooltip || !isMockTriggerAvailable}
          buttonLabel={'Trigger mock'}
          tooltipContent={mockTriggerMessage || mockRunCapacityExceededMessage}
        />
      </Button>
    </div>
  );
};

export default SubmitCta;
